import type { BackInStockSubscriber } from "@redotech/redo-customer-common/customer-product-notification/customer-product-notification";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import EmailIcon from "@redotech/redo-icons/mail-02.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import {
  RedoTable,
  RedoTableLoadingStateContent,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { getDateEndOfDay } from "@redotech/util/date";
import { memo, type ReactNode } from "react";
import { useDebounce } from "usehooks-ts";
import type { BisFilterProps } from "./filter-props";
import { SearchAndFilterBar } from "./search-filter-bar";

export const SubscribersList = memo(function SubscribersList({
  productId,
  variantId,
  showVariantColumn,
  subscriberSearch,
  setSubscriberSearch,
  footer,
  ...filterProps
}: {
  productId: string;
  variantId?: string;
  showVariantColumn: boolean;
  subscriberSearch: string;
  setSubscriberSearch: (q: string) => void;
  footer?: (
    subscribers: BackInStockSubscriber[],
    totalCount: number,
  ) => ReactNode;
} & BisFilterProps) {
  const { dateFilter, channelFilters, statusFilters } = filterProps;
  const debouncedSearch = useDebounce(subscriberSearch, 500);

  const { data: subscribersData, isLoading } = useRpcQuery(
    RedoMarketingRpcClientContext,
    {
      method: "getBackInStockSubscribers",
      args: {
        productId,
        variantId,
        searchQuery: debouncedSearch || undefined,
        statuses: statusFilters,
        channels: channelFilters,
        dateFrom: dateFilter ? new Date(dateFilter[0].toString()) : undefined,
        dateTo: dateFilter
          ? getDateEndOfDay(new Date(dateFilter[1].toString()))
          : undefined,
      },
      options: { placeholderData: (prev) => prev },
    },
  );

  const subscribers: BackInStockSubscriber[] =
    subscribersData?.subscribers || [];
  const totalCount = subscribersData?.totalCount || 0;
  const layout = buildSubscriberColumns(showVariantColumn);

  return (
    <Flex dir="column" flex="1" gap="none" overflow="hidden">
      <Flex align="center" gap="md" justify="space-between" px="xl" py="md">
        <SearchAndFilterBar
          {...filterProps}
          placeholder="Search subscribers..."
          searchQuery={subscriberSearch}
          setSearchQuery={setSubscriberSearch}
        />
      </Flex>

      <Flex dir="column" flex={1} gap="none" overflow="auto">
        <RedoTable<BackInStockSubscriber>
          emptyStateContent={
            !isLoading && subscribers.length === 0 ? (
              <Flex align="center" flex={1} justify="center">
                <RedoEmptyState
                  Icon={EmailIcon}
                  subtext="Subscribers will appear here when customers sign up for back in stock notifications."
                  text="No subscribers found"
                />
              </Flex>
            ) : undefined
          }
          layout={layout}
          loadingStateContent={
            isLoading ? (
              <Flex align="center" justify="center">
                <RedoTableLoadingStateContent />
              </Flex>
            ) : undefined
          }
          rowsOfData={subscribers}
          size="sm"
        />
      </Flex>

      <Divider />
      <Flex align="center" justify="space-between" px="xl" py="md">
        <Text fontSize="sm" textColor="secondary">
          Showing {subscribers.length} of {totalCount}
        </Text>
        {footer?.(subscribers, totalCount)}
      </Flex>
    </Flex>
  );
});

function buildSubscriberColumns(showVariantColumn: boolean) {
  const columns: RedoTableColumn<BackInStockSubscriber>[] = [
    {
      widthMode: { type: "fill" },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Name" }),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={row.name || "\u2014"} />
      ),
    },
    {
      widthMode: { type: "fill" },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Email" }),
      renderNormalCell: ({ row }) => <ChannelCell value={row.email} />,
    },
    {
      widthMode: { type: "fill" },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "SMS" }),
      renderNormalCell: ({ row }) => <ChannelCell value={row.phone} />,
    },
  ];

  if (showVariantColumn) {
    columns.push({
      widthMode: { type: "fill" },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Variant(s)" }),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={row.variantTitle || row.variantId || ""} />
      ),
    });
  }

  columns.push({
    widthMode: { type: "fill" },
    renderHeaderCell: () =>
      StandardRedoTableHeaderCell({ headerText: "Status" }),
    renderNormalCell: ({ row }) => (
      <Flex p="xl">
        <RedoBadge
          color={row.status === "waiting" ? "warning" : "success"}
          text={
            row.status === "waiting"
              ? `Waiting ${row.waitingDays} day${row.waitingDays !== 1 ? "s" : ""}`
              : `Notified ${row.notifiedDate?.toLocaleDateString()}`
          }
        />
      </Flex>
    ),
  });

  return { columns, headerColor: "white" as const };
}

function ChannelCell({ value }: { value: string | undefined }) {
  if (value) {
    return (
      <Flex align="center" gap="sm" overflow="hidden" p="xl">
        <RedoIcon color="success" Icon={CheckCircleIcon} size={16} />
        <RedoTableTextCell text={value} />
      </Flex>
    );
  }
  return (
    <Flex align="center" p="xl">
      <RedoIcon color="tertiary" Icon={XCircleIcon} size={16} />
    </Flex>
  );
}
